import { Vec2 } from '../../math/vec2.js';
import { toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { Language } from '../language.js';
import { mapToData, cloneMap, isSameMap, isSameData, SerializedData } from '../../dto/dto.js';
import { SeatingElement } from './seating-element.js';

export class SeatingLabel extends SeatingElement {
  static readonly TYPE_ID: number = 0x04;

  constructor(
      uniqueId: number,
      enabled: boolean,
      public readonly texts: Map<Language, string>,
      public readonly textSize: number = 1.0,
      public readonly color: number | null = null,
      position: Vec2 | null = null,
      rotation: number | null = null,
      parent: WeakRef<SeatingElement> | null = null,
  ) {
    super(
        uniqueId,
        enabled,
        position ?? new Vec2(0, 0),
        rotation ?? 0,
        parent,
    );
  }

  get $typeId(): number {
    return SeatingLabel.TYPE_ID;
  }

  static fromJsonOrNull(data: SerializedData | null): SeatingLabel | null {
    return isNullOrUndefined(data) ? null : SeatingLabel.fromJson(data);
  }

  static fromJson(data: SerializedData): SeatingLabel {
    return new SeatingLabel(
        MapUtils.getNumber(data, 'uniqueId'),
        MapUtils.get<boolean>(data, 'enabled'),
        MapUtils.getMap<Language, string>(data, 'texts', (k, v) => ({
          key: toEnumFromString(k, Language),
          value: v as string,
        })),
        MapUtils.getNumber(data, 'textSize'),
        MapUtils.getNumberOrNull(data, 'color'),
        Vec2.fromJsonOrNull(MapUtils.getOrNull<SerializedData>(data, 'position')),
        MapUtils.getNumberOrNull(data, 'rotation'),
    );
  }

  toJson(): SerializedData {
    return {
      $typeId: this.$typeId,
      uniqueId: this.uniqueId,
      enabled: this.enabled,
      texts: mapToData(this.texts, (k, v) => ({ key: k.toString(), value: v })),
      textSize: this.textSize,
      color: this.color ?? null,
      position: this.position?.toJson() ?? null,
      rotation: this.rotation ?? null,
    };
  }

  clone(parent: WeakRef<SeatingElement> | null): this {
    return new SeatingLabel(
        this.uniqueId,
        this.enabled,
        cloneMap(this.texts),
        this.textSize,
        this.color,
        this.position?.clone() ?? null,
        this.rotation,
        parent,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.enabled === other.enabled &&
        isSameMap(this.texts, other.texts) &&
        this.textSize === other.textSize &&
        this.color === other.color &&
        isSameData(this.position, other.position) &&
        this.rotation === other.rotation;
  }
}
